import browserLocalstorage from 'browser-localstorage-expire';
import { getUser } from '~src/user';
import {
  fetchDisabledOrgFeatures,
  fetchDisabledUserFeatures,
} from './~graphql/controllers/fetchFeatures';
import editorConfig from '~config/editorConfig';

const localCache = browserLocalstorage();

export const FEATURES = {
  CREATE_DEFUNCT: 'servizi.create',
  CREATE_TEMPLATES_CATEGORIES: 'template_categories.create',
  CREATE_TEMPLATES: 'template.create',
  CLONE_TEMPLATES: 'template.clone',
  DELETE_TEMPLATES: 'template.delete',
  CREATE_FORMATS: 'formati.create',
  CREATE_PROJECT_TYPES: 'projects.create',
  ADD_PAGES: 'pages.create',
  CREATE_VIDEOS: 'videos.create',
  VIDEOS_ADVANCED_CREATOR_MODE: 'video_advanced_creator_mode.show',
  CREATE_CARDS: 'cards.create',
  AI_USE_VTO: 'ai.use_vto',
  AI_USE_SMILE: 'ai.use_smile',
  AI_USE_COLORIZE_BW: 'ai.use_colorizebw',
  AI_USE_AUTOCOLOR: 'ai.use_autocolor',
  AI_USE_EXPAND: 'ai.use_expand',
  AI_USE_FACE_ENHANCEMENT: 'ai.migliora_viso',
  AI_USE_MAKE_LOOK_YOUNGER: 'ai.filtro_bellezza',
  AI_USE_IMAGE_ENHANCEMENT: 'ai.migliora_foto',
  AI_USE_MAGIC_ERASER: 'ai.use_magic_eraser',
  SPELLCHECK: 'spellcheck.show',
  AI_USE_AI_EDIT: 'ai.use_ai_edit',
  AI_USE_AI_BG: 'ai.use_ai_bg',
  CREATE_TEMPLATE_FROM_IMPORT: 'giotto.create_template_from_import',
};

export const getDisabledFeatures = async (ignoreCache = false) => {
  const superUserFeatureArray = [
    'template.create',
    'template.clone',
    'template.delete',
    'template_categories.create',
    'projects.create',
    'formati.create',
    'pages.create',
  ];
  let disabledFeatures, userFeatures;
  if (ignoreCache) {
    localStorage.removeItem('disabledFeatures');
    localStorage.removeItem('userFeatures');
  }
  const cachedDisabledFeatures = localCache.getItem('disabledFeatures');
  const cachedUserFeatures = localCache.getItem('userFeatures');

  if (cachedDisabledFeatures) {
    disabledFeatures = cachedDisabledFeatures;
    userFeatures = cachedUserFeatures;
  }

  const { user, ofcSuperUser } = await getUser();
  const isSuperUser =
    ofcSuperUser === true ||
    (user && editorConfig.superUserIds.includes(user.objectId));
  const orgId = user.org_pointer.objectId;
  const plan = user.org_pointer.tipo_licenza;

  if (!disabledFeatures) {
    disabledFeatures = await fetchDisabledOrgFeatures(orgId, plan);
    if (isSuperUser) {
      disabledFeatures = disabledFeatures.filter((feature) => {
        return !superUserFeatureArray.includes(feature.name);
      });
    }
    localCache.setItem(
      'disabledFeatures',
      disabledFeatures,
      editorConfig.localStorageExpiryInMinutes
    );
  }

  if (!userFeatures) {
    userFeatures = await fetchDisabledUserFeatures(orgId, user.objectId);
    if (isSuperUser) {
      userFeatures = userFeatures.filter((feature) => {
        // user features use `key`
        return !superUserFeatureArray.includes(feature.key);
      });
    }
    localCache.setItem(
      'userFeatures',
      userFeatures,
      editorConfig.localStorageExpiryInMinutes
    );
  }

  if (isSuperUser) {
    disabledFeatures = (disabledFeatures || []).filter((feature) => {
      return !superUserFeatureArray.includes(feature.name);
    });
    userFeatures = (userFeatures || []).filter((feature) => {
      // user features use `key`
      return !superUserFeatureArray.includes(feature.key);
    });
  }

  return { disabledFeatures, userFeatures };
};

export const isFeatureUserDisabled = async (featureName) => {
  const { userFeatures } = await getDisabledFeatures();
  const [featureRoot, action] = featureName.split('.');
  if (action !== 'show') {
    for (const uf of userFeatures || []) {
      if (
        (uf.key === featureName && uf.disabled === true) ||
        (uf.key === `${featureRoot}.show` && uf.disabled === true)
      ) {
        return true;
      }
    }
    return false;
  } else {
    for (const uf of userFeatures || []) {
      if (uf.key === featureName && uf.disabled === true) {
        return true;
      }
    }
    return false;
  }
};

export const isFeatureUserEnabled = async (featureName) => {
  const { userFeatures } = await getDisabledFeatures();
  const [featureRoot, action] = featureName.split('.');
  if (action !== 'show') {
    for (const uf of userFeatures || []) {
      if (
        (uf.key === featureName && uf.disabled === false) ||
        (uf.key === `${featureRoot}.show` && uf.disabled === false)
      ) {
        return true;
      }
    }
    return false;
  } else {
    for (const uf of userFeatures || []) {
      if (uf.key === featureName && uf.disabled === false) {
        return true;
      }
    }
    return false;
  }
};

export const checkFeaturePreference = async (
  featureName,
  ignoreCache = false
) => {
  const splitedFeature = featureName.split('.');
  if (splitedFeature.length == 1) {
    featureName = featureName + '.show';
  }

  const { disabledFeatures } = await getDisabledFeatures(ignoreCache);
  const selectedFeature = disabledFeatures.find(
    (feat) => feat.name == featureName
  );
  const result = {
    action: splitedFeature[1] ? splitedFeature[1] : 'show',
    style: selectedFeature?.style,
    user: true,
    org: true,
    plan: 'included',
    module: 'included',
    feature: splitedFeature[0],
  };
  // Explicit user-enabled override
  if (await isFeatureUserEnabled(featureName)) {
    return result;
  }

  // Explicit user-disabled preference
  if (await isFeatureUserDisabled(featureName)) {
    result.user = false;
    if (!result.style) {
      result.style = 'hidden';
    }
    return result;
  }

  // Org-level checks
  if (selectedFeature) {
    if (selectedFeature.plans && selectedFeature.plans.length > 0) {
      result.plan = 'not_included';
      result.org = false;
      result.module = 'not_included';
      if (!result.style) {
        result.style = 'opacity';
      }
      return result;
    } else if (selectedFeature.module_pointer) {
      result.module = 'not_included';
      result.moduleInfo = selectedFeature.module_pointer;
      result.org = false;
      if (!result.style) {
        result.style = 'opacity';
      }
      return result;
    } else {
      if (!result.style) {
        result.style = 'hidden';
      }
      result.org = false;
      result.plan = 'empty';
      result.module = 'empty';
      return result;
    }
  }

  return result;
};

export const isFeatureEnabled = async (featureName, ignoreCache = false) => {
  const feature = await checkFeaturePreference(featureName, ignoreCache);
  return feature.user && feature.org;
};
